import NumberFormatCurrency from "components/NumberFormatCurrency";
import { Button, Col, Form, Input, Modal, Row } from "antd";
import { getFormSnakeCaseError } from "utils/getFormError";
import useNotification from "utils/hooks/useNotification";
import { ApolloQueryResult } from "@apollo/client";
import TooltipButton from "views/Documents/components/TooltipButton";
import { MdEdit } from "react-icons/md";
import { useTranslation } from "react-i18next";
import useVatsApi from "../hooks/useVatsApi";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { FaSave } from "react-icons/fa";
import React from "react";

interface CreateUpdateVatPropsTypes {
  id?: ID;
  data?: GetClientInfoResponseData | undefined;
  refetch: (
    variables?: Partial<ListVatsVars> | undefined,
  ) => Promise<ApolloQueryResult<ListVatsResponse>>;
}

export default function CreateUpdateVat(props: CreateUpdateVatPropsTypes): React.ReactElement {
  const { id, refetch } = props;

  const urlParams: RouteParams = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const { getVat, createUpdateVat } = useVatsApi(id, form);
  const { notificationSuccessfullySaved } = useNotification();

  useEffect(() => {
    if (id && isModalVisible) {
      getVat({
        variables: {
          id: id,
        },
      });
    }
  }, [isModalVisible]);

  const onFinish = async (values: Record<string, any>) => {
    const updateId = id ? { id } : {};
    const queryName = id ? "updateVat" : "createVat";
    const variables = {
      input: {
        companyId: urlParams.companyId,
        code: values.code,
        name: values.name,
        rate: Number(values.rate) / 100,
        ...updateId,
      },
    };

    const response = await createUpdateVat({ variables });

    const createErrors = response?.data?.createVat?.errors;
    const updateErrors = response?.data?.updateVat?.errors;

    if ((response?.data?.[queryName]?.errors?.length ?? 0) > 0) {
      const fieldsData = getFormSnakeCaseError(createErrors || updateErrors, values);
      form.setFields(fieldsData);
    } else {
      refetch();
      notificationSuccessfullySaved(t("VAT"));
      setIsModalVisible(false);
      form.resetFields();
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.submit();
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      {id ? (
        <TooltipButton placement="top" handleOnClick={showModal} icon={<MdEdit />} />
      ) : (
        <Row justify="end" className="margin-top">
          <Button type="primary" onClick={showModal}>
            {t("Add")}
          </Button>
        </Row>
      )}
      <Modal
        title={id ? t("Update VAT") : t("Add VAT")}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t("Save")}
        width={768}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        className="antd-modal"
      >
        <Form
          name="createUpdateVat"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="code"
                rules={[{ required: true, message: t("This field is required") }]}
                label={t("Code")}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="rate"
                label={t("Rate")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <NumberFormatCurrency addonAfter="%" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="name"
                rules={[{ required: true, message: t("This field is required") }]}
                label={t("Name")}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
